/**
 * @hues/router — Hues.js Official Router
 * -----------------------------------------------------------------------------
 * A hash/history-mode router for Hues.js. Supports:
 *   • Hash mode (default) and HTML5 history mode
 *   • Nested routes with `<router-view>` style outlet
 *   • Dynamic route params (`/users/:id`)
 *   • Query and hash parsing
 *   • Programmatic navigation (`router.push`, `router.replace`, `router.go`)
 *   • Navigation guards (`beforeEach`, `beforeResolve`, `afterEach`)
 *   • Per-route `beforeEnter` guards
 *   • Lazy-loaded route components
 *   • Named routes and `router-link` helper
 *
 * © Death Legion — Hues.js
 */

import { ref, computed, reactive, Ref, effect } from '../core/reactivity'
import { h, VNode, Component, Fragment } from '../core/vdom'
import { provide, inject, getCurrentInstance, onMounted, onUnmounted } from '../core/component'

export interface RouteRecord {
  path: string
  name?: string
  component?: Component | (() => Promise<Component>)
  children?: RouteRecord[]
  redirect?: string
  beforeEnter?: (to: RouteLocation, from: RouteLocation, next: (v?: any) => void) => void
  meta?: Record<string, any>
  props?: boolean | Record<string, any>
}

export interface RouteLocation {
  path: string
  name?: string
  params: Record<string, string>
  query: Record<string, string>
  hash: string
  matched: RouteRecord[]
  meta: Record<string, any>
}

export interface RouterOptions {
  history?: 'hash' | 'history'
  routes: RouteRecord[]
  base?: string
  scrollBehavior?: (to: RouteLocation, from: RouteLocation) => void
}

export interface Router {
  mode: 'hash' | 'history'
  currentRoute: Ref<RouteLocation>
  install: (app: any) => void
  push: (to: string | { path?: string; name?: string; query?: Record<string, any>; params?: Record<string, any> }) => Promise<void>
  replace: (to: string | { path?: string; name?: string; query?: Record<string, any>; params?: Record<string, any> }) => Promise<void>
  go: (n: number) => void
  back: () => void
  forward: () => void
  beforeEach: (guard: (to: RouteLocation, from: RouteLocation, next: (v?: any) => void) => void) => () => void
  beforeResolve: (guard: (to: RouteLocation, from: RouteLocation, next: (v?: any) => void) => void) => () => void
  afterEach: (hook: (to: RouteLocation, from: RouteLocation) => void) => () => void
  addRoute: (parentName: string | null, route?: RouteRecord) => void
  removeRoute: (name: string) => void
  getRoutes: () => RouteRecord[]
  hasRoute: (name: string) => boolean
  resolve: (to: string | { path?: string; name?: string; query?: Record<string, any>; params?: Record<string, any> }) => RouteLocation
}

const ROUTER_KEY = Symbol('hues-router')

// ----- Route matching --------------------------------------------------------

interface CompiledRoute {
  record: RouteRecord
  regex: RegExp
  keys: string[]
  parent?: CompiledRoute
}

function compileRoute(record: RouteRecord, parent?: CompiledRoute): CompiledRoute {
  const keys: string[] = []
  const fullPath = parent ? parent.record.path.replace(/\/$/, '') + '/' + record.path.replace(/^\//, '') : record.path
  const pattern = fullPath
    .replace(/\/:([^/]+)/g, (_, k) => {
      keys.push(k)
      return '/([^/]+)'
    })
    .replace(/\*/g, '.*')
  const regex = new RegExp(`^${pattern}$`)
  return { record: { ...record, path: fullPath }, regex, keys, parent }
}

function flattenRoutes(routes: RouteRecord[], parent?: CompiledRoute): CompiledRoute[] {
  const result: CompiledRoute[] = []
  for (const r of routes) {
    const compiled = compileRoute(r, parent)
    result.push(compiled)
    if (r.children) {
      result.push(...flattenRoutes(r.children, compiled))
    }
  }
  return result
}

function matchRoute(routes: CompiledRoute[], path: string): RouteLocation {
  // Find the longest chain of matched routes (parent + child)
  for (let i = routes.length - 1; i >= 0; i--) {
    const route = routes[i]
    const m = route.regex.exec(path)
    if (m) {
      const params: Record<string, string> = {}
      route.keys.forEach((k, idx) => {
        params[k] = decodeURIComponent(m[idx + 1])
      })
      // Build the matched chain
      const matched: RouteRecord[] = []
      let current: CompiledRoute | undefined = route
      while (current) {
        matched.unshift(current.record)
        current = current.parent
      }
      const meta = matched.reduce((acc, r) => ({ ...acc, ...(r.meta || {}) }), {})
      return {
        path,
        name: route.record.name,
        params,
        query: {},
        hash: '',
        matched,
        meta,
      }
    }
  }
  return {
    path,
    params: {},
    query: {},
    hash: '',
    matched: [],
    meta: {},
  }
}

// ----- Router factory --------------------------------------------------------

export function createRouter(options: RouterOptions): Router {
  const mode = options.history || 'hash'
  const base = options.base || '/'
  const compiledRoutes = flattenRoutes(options.routes)
  const beforeGuards: Array<(to: RouteLocation, from: RouteLocation, next: (v?: any) => void) => void> = []
  const resolveGuards: Array<(to: RouteLocation, from: RouteLocation, next: (v?: any) => void) => void> = []
  const afterHooks: Array<(to: RouteLocation, from: RouteLocation) => void> = []

  // During SSR, `window` doesn't exist — fall back to a safe default route.
  const isBrowser = typeof window !== 'undefined'
  const currentRoute = ref<RouteLocation>(isBrowser ? getCurrentRoute() : {
    path: '/', params: {}, query: {}, hash: '', matched: [], meta: {},
  })

  function getCurrentRoute(): RouteLocation {
    if (!isBrowser) {
      return { path: '/', params: {}, query: {}, hash: '', matched: [], meta: {} }
    }
    let path: string
    if (mode === 'hash') {
      const hash = window.location.hash.slice(1) || '/'
      const [pathPart, queryPart] = hash.split('?')
      path = pathPart || '/'
      const query: Record<string, string> = {}
      if (queryPart) {
        new URLSearchParams(queryPart).forEach((v, k) => { query[k] = v })
      }
      const loc = matchRoute(compiledRoutes, path)
      loc.query = query
      return loc
    } else {
      path = window.location.pathname.slice(base.length - 1) || '/'
      const query: Record<string, string> = {}
      new URLSearchParams(window.location.search).forEach((v, k) => { query[k] = v })
      const loc = matchRoute(compiledRoutes, path)
      loc.query = query
      loc.hash = window.location.hash
      return loc
    }
  }

  function navigate(to: RouteLocation): Promise<void> {
    const from = currentRoute.value
    return new Promise((resolve) => {
      const runGuards = (guards: typeof beforeGuards, idx: number, next: (v?: any) => void) => {
        if (idx >= guards.length) return next()
        const guard = guards[idx]
        guard(to, from, (v) => {
          if (v === false) return resolve()
          if (typeof v === 'string' || (v && typeof v === 'object')) {
            navigate(typeof v === 'string' ? matchRoute(compiledRoutes, v) : v as RouteLocation).then(resolve)
            return
          }
          runGuards(guards, idx + 1, next)
        })
      }
      runGuards([...beforeGuards, ...(to.matched[0]?.beforeEnter ? [to.matched[0].beforeEnter!] : [])], 0, async () => {
        // Resolve lazy components
        for (const r of to.matched) {
          if (r.component && typeof r.component === 'function' && !(r.component as any).displayName) {
            const loader = r.component as () => Promise<Component>
            r.component = await loader()
          }
        }
        runGuards(resolveGuards, 0, () => {
          currentRoute.value = to
          afterHooks.forEach((h) => h(to, from))
          resolve()
        })
      })
    })
  }

  function push(to: string | { path?: string; name?: string; query?: Record<string, any>; params?: Record<string, any> }): Promise<void> {
    const loc = resolve(to)
    if (mode === 'hash') {
      const queryStr = Object.keys(loc.query).length ? '?' + new URLSearchParams(loc.query as any).toString() : ''
      window.location.hash = loc.path + queryStr
    } else {
      const queryStr = Object.keys(loc.query).length ? '?' + new URLSearchParams(loc.query as any).toString() : ''
      window.history.pushState({}, '', base.replace(/\/$/, '') + loc.path + queryStr + loc.hash)
    }
    return navigate(loc)
  }

  function replace(to: string | { path?: string; name?: string; query?: Record<string, any>; params?: Record<string, any> }): Promise<void> {
    const loc = resolve(to)
    if (mode === 'hash') {
      const queryStr = Object.keys(loc.query).length ? '?' + new URLSearchParams(loc.query as any).toString() : ''
      const url = window.location.pathname + window.location.search + '#' + loc.path + queryStr
      window.history.replaceState({}, '', url)
    } else {
      const queryStr = Object.keys(loc.query).length ? '?' + new URLSearchParams(loc.query as any).toString() : ''
      window.history.replaceState({}, '', base.replace(/\/$/, '') + loc.path + queryStr + loc.hash)
    }
    return navigate(loc)
  }

  function go(n: number): void { window.history.go(n) }
  function back(): void { window.history.back() }
  function forward(): void { window.history.forward() }

  function beforeEach(guard: (to: RouteLocation, from: RouteLocation, next: (v?: any) => void) => void): () => void {
    beforeGuards.push(guard)
    return () => { const i = beforeGuards.indexOf(guard); if (i > -1) beforeGuards.splice(i, 1) }
  }
  function beforeResolve(guard: (to: RouteLocation, from: RouteLocation, next: (v?: any) => void) => void): () => void {
    resolveGuards.push(guard)
    return () => { const i = resolveGuards.indexOf(guard); if (i > -1) resolveGuards.splice(i, 1) }
  }
  function afterEach(hook: (to: RouteLocation, from: RouteLocation) => void): () => void {
    afterHooks.push(hook)
    return () => { const i = afterHooks.indexOf(hook); if (i > -1) afterHooks.splice(i, 1) }
  }

  function addRoute(parentName: string | null, route?: RouteRecord): void {
    if (route) {
      const parent = parentName ? compiledRoutes.find((r) => r.record.name === parentName) : undefined
      compiledRoutes.push(...flattenRoutes([route], parent))
    }
  }
  function removeRoute(name: string): void {
    const idx = compiledRoutes.findIndex((r) => r.record.name === name)
    if (idx > -1) compiledRoutes.splice(idx, 1)
  }
  function getRoutes(): RouteRecord[] {
    return compiledRoutes.map((r) => r.record)
  }
  function hasRoute(name: string): boolean {
    return compiledRoutes.some((r) => r.record.name === name)
  }
  function resolve(to: string | { path?: string; name?: string; query?: Record<string, any>; params?: Record<string, any> }): RouteLocation {
    if (typeof to === 'string') {
      const [path, queryStr] = to.split('?')
      const query: Record<string, string> = {}
      if (queryStr) new URLSearchParams(queryStr).forEach((v, k) => { query[k] = v })
      const loc = matchRoute(compiledRoutes, path)
      loc.query = query
      return loc
    }
    if (to.name) {
      const route = compiledRoutes.find((r) => r.record.name === to.name)
      if (route) {
        let path = route.record.path
        if (to.params) {
          for (const k in to.params) path = path.replace(`:${k}`, String(to.params[k]))
        }
        const loc = matchRoute(compiledRoutes, path)
        loc.query = (to.query || {}) as any
        return loc
      }
    }
    if (to.path) {
      const loc = matchRoute(compiledRoutes, to.path)
      loc.query = (to.query || {}) as any
      return loc
    }
    return currentRoute.value
  }

  // Listen for popstate / hashchange
  if (typeof window !== 'undefined') {
    const onChange = () => navigate(getCurrentRoute())
    if (mode === 'hash') {
      window.addEventListener('hashchange', onChange)
    } else {
      window.addEventListener('popstate', onChange)
    }
  }

  const createdRouter: Router = {
    mode,
    currentRoute,
    push,
    replace,
    go,
    back,
    forward,
    beforeEach,
    beforeResolve,
    afterEach,
    addRoute,
    removeRoute,
    getRoutes,
    hasRoute,
    resolve,
    // install is assigned below — it needs to reference `createdRouter` itself
    // via closure, which only works after the object literal is fully built.
    install(_app: any) { /* patched below */ },
  }

  createdRouter.install = function (app: any) {
    app.provide(ROUTER_KEY, createdRouter)
    app.config.globalProperties.$router = createdRouter
    app.config.globalProperties.$route = currentRoute
    app.component('RouterLink', {
      props: { to: { type: [String, Object], required: true } },
      setup(props: any) {
        return () => {
          const loc = typeof props.to === 'string' ? resolve(props.to) : resolve(props.to)
          const isActive = computed(() => currentRoute.value.path === loc.path)
          return h('a', {
            href: (mode === 'hash' ? '#' : '') + loc.path,
            class: { 'router-link-active': isActive.value, 'router-link-exact-active': isActive.value },
            onClick: (e: Event) => {
              e.preventDefault()
              push(props.to)
            },
          })
        }
      },
    })
    app.component('RouterView', {
      setup() {
        return () => {
          const route = currentRoute.value
          if (!route.matched.length) return h(Fragment, null, [])
          const deepest = route.matched[route.matched.length - 1]
          if (!deepest.component) return h(Fragment, null, [])
          return h(deepest.component, route.params)
        }
      },
    })
  }

  return createdRouter
}

export function useRouter(): Router {
  return inject(ROUTER_KEY) as Router
}

export function useRoute(): Ref<RouteLocation> {
  return inject(ROUTER_KEY + '-route') as Ref<RouteLocation>
}

// ----- Programmatic helper: RouterLink / RouterView as components -------------

export const RouterLink: Component = {
  name: 'RouterLink',
  props: { to: { type: [String, Object], required: true } },
  setup(props: any, ctx: any) {
    const router = useRouter()
    return () => {
      // Render the <a> element with the default slot children inside.
      const slotChildren = ctx?.slots?.default ? ctx.slots.default() : []
      return h('a', {
        href: typeof props.to === 'string' ? props.to : props.to.path,
        onClick: (e: Event) => {
          e.preventDefault()
          router.push(props.to)
        },
      }, slotChildren)
    }
  },
}

export const RouterView: Component = {
  name: 'RouterView',
  setup() {
    const router = useRouter()
    return () => {
      if (!router) return h(Fragment, null, [])
      const route = router.currentRoute.value
      if (!route.matched.length) return h(Fragment, null, [])
      const deepest = route.matched[route.matched.length - 1]
      if (!deepest.component) return h(Fragment, null, [])
      return h(deepest.component, route.params)
    }
  },
}
